import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ToastProvider } from '@/context/ToastContext'
import { AuthProvider } from '@/context/AuthContext'
import { FamilyProvider } from '@/context/FamilyContext'
import { StudioProvider } from '@/context/StudioContext'
import { WritingStudioProvider } from '@/context/WritingStudioContext'
import { ToastManager } from '@/components/ui'
import { DevModePanel } from '@/components/DevModePanel'
import { RequireAuth, RequireWriter, RequireOnboarding, RequireAdmin, RequireNotBlocked, RequireFamily } from '@/components/auth'
import { AppLayout } from '@/components/layout'
import { AdminProvider } from '@/context/AdminContext'
import { Landing } from '@/pages/Landing'
import { NotFound } from '@/pages/NotFound'
import { Login } from '@/pages/Login'
import { SelectWriter } from '@/pages/SelectWriter'
import { Dashboard } from '@/pages/Dashboard'
import { MyStories } from '@/pages/MyStories'
import { QuickWrite } from '@/pages/QuickWrite'
import { WriteStory } from '@/pages/WriteStory'
import { HowItWorks } from '@/pages/HowItWorks'
import { Onboarding } from '@/pages/Onboarding'
import { Upgrade } from '@/pages/Upgrade'
import { Admin } from '@/pages/Admin'
import { RequestAccess } from '@/pages/RequestAccess'
import { DirectLogin } from '@/pages/DirectLogin'
import { AuthCallback } from '@/pages/AuthCallback'
import { BetaRequests } from '@/pages/admin/BetaRequests'
import { Waitlist } from '@/pages/admin/Waitlist'
import { ChallengeHub, ChallengeDetail, ChallengeResults } from '@/pages/challenge'
import { RevisionHub, RevisionEditor } from '@/pages/revise'
import { CharacterLibrary, CharacterWizard } from '@/pages/characters'
import { DetectiveHub, DetectiveStory } from '@/pages/detective'
import { StudioLanding, ProjectList, ProjectEditor, LearnHub } from '@/pages/studio'
import { Help } from '@/pages/Help'
import { StorytellingProvider } from '@/context/StorytellingContext'
import { StorytellingLayout } from '@/components/layout/StorytellingLayout'
import { StorytellingHome, LessonPage, SecretsPage, BranchesPlayground } from '@/pages/storytelling'
import { TourProvider } from '@/components/tour'

function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <FamilyProvider>
            <StudioProvider>
              <WritingStudioProvider>
              <TourProvider>
              <Routes>
              {/* Public routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Navigate to="/login" replace />} />
              <Route path="/request-access" element={<RequestAccess />} />
              <Route path="/direct-login" element={<DirectLogin />} />
              <Route path="/auth/callback" element={<AuthCallback />} />

              {/* Auth required - select writer */}
              <Route
                path="/select-writer"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireFamily>
                        <SelectWriter />
                      </RequireFamily>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />

              {/* Auth + writer required - onboarding */}
              <Route
                path="/onboarding"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <Onboarding />
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />

              {/* Auth + writer required - upgrade page */}
              <Route
                path="/upgrade"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <Upgrade />
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />

              {/* Protected app routes with shared layout */}
              <Route
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <AppLayout />
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              >
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/stories" element={<MyStories />} />
                <Route path="/quick-write" element={<QuickWrite />} />
              </Route>

              {/* Story editor - uses its own specialized header */}
              <Route
                path="/stories/:storyId"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <WriteStory />
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />

              {/* Challenge Lab routes */}
              <Route
                path="/challenge"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <ChallengeHub />
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />
              <Route
                path="/challenge/:challengeId"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <ChallengeDetail />
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />
              <Route
                path="/challenge/:challengeId/submission/:submissionId"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <ChallengeResults />
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />

              {/* Revision Studio routes */}
              <Route
                path="/revise"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <RevisionHub />
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />
              <Route
                path="/revise/:sourceType/:sourceId"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <RevisionEditor />
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />

              {/* Character Builder routes */}
              <Route
                path="/characters"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <CharacterLibrary />
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />
              <Route
                path="/characters/new"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <CharacterWizard />
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />
              <Route
                path="/characters/:characterId"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <CharacterWizard />
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />

              {/* Story Detective routes */}
              <Route
                path="/detective"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <DetectiveHub />
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />
              <Route
                path="/detective/:storyId"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <DetectiveStory />
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />

              {/* Storytelling routes */}
              <Route
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <StorytellingProvider>
                            <StorytellingLayout />
                          </StorytellingProvider>
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              >
                <Route path="/storytelling" element={<StorytellingHome />} />
                <Route path="/storytelling/lesson/:lessonId" element={<LessonPage />} />
                <Route path="/storytelling/secrets" element={<SecretsPage />} />
                <Route path="/storytelling/play/branches" element={<BranchesPlayground />} />
              </Route>

              {/* Writing Studio routes */}
              <Route
                path="/studio"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <StudioLanding />
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />
              <Route
                path="/studio/write"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <ProjectList />
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />
              <Route
                path="/studio/write/:projectId"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <ProjectEditor />
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />
              <Route
                path="/studio/learn"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <RequireWriter>
                        <RequireOnboarding>
                          <LearnHub />
                        </RequireOnboarding>
                      </RequireWriter>
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />

              {/* Admin panel - admin only */}
              <Route
                path="/admin"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <AdminProvider>
                        <Admin />
                      </AdminProvider>
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/beta-requests"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <BetaRequests />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />
              <Route
                path="/admin/waitlist"
                element={
                  <RequireAuth>
                    <RequireAdmin>
                      <Waitlist />
                    </RequireAdmin>
                  </RequireAuth>
                }
              />

              {/* Help page */}
              <Route
                path="/help"
                element={
                  <RequireAuth>
                    <RequireNotBlocked>
                      <Help />
                    </RequireNotBlocked>
                  </RequireAuth>
                }
              />

              {/* Fallback */}
              <Route path="*" element={<NotFound />} />
              </Routes>
              <ToastManager />
              <DevModePanel />
              </TourProvider>
              </WritingStudioProvider>
            </StudioProvider>
          </FamilyProvider>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  )
}

export default App
